import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { supabase } from '../../lib/supabase';
import { getSdrDay, getSdrOptions, getSdrChecklist, saveSdrChecklist, getMinariActivity, type SdrDay, type SdrDayClient, type SdrOption, type SdrType, type MinariActivity } from '../../lib/refonteApi';
import { SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

// Objectif V1 (décision 24/09) : CONNECTÉS 30 (cold call) / 15 par client (GTM),
// unifié avec le cockpit Production SDR. L'échange (>30s) reste affiché comme repère.
const COLD_TARGET = 30;   // connectés / jour (cold call)
const GTM_TARGET = 15;    // connectés / jour / client (GTM)
const TYPE_LABEL: Record<SdrType, string> = { cold_call: 'Cold call', gtm: 'GTM', mixte: 'Mixte', '?': '—' };

// Déroulé du jour (cerveau) — checklist gamifiée, adaptée pack vs full stack.
const COLD_STEPS = ['Saluer le client (WhatsApp/Slack)', 'Confirmer les RDV du jour', 'Partager l’état des RDV aux clients', 'Vérifier le stock de liste', 'Prospection : chaud → no-shows → froid', 'Point mi-journée : réécoute', 'Débrief client + stock prochaine session'];
// « Point 14 h en binôme » retiré (réunion 10/09 : ils se parlent toute la journée) → créneau Kevin.
const GTM_STEPS = ['Confirmer les RDV du jour', 'Regarder Instantly & HeyReach (nuit)', 'Vérifier les envois à pleine capacité', 'Prospection : chaud → froid', 'Créneau GTM 14h-15h30 avec Kevin (si besoin, sur inscription)', 'Reprise + débrief des clients'];

const isoLocal = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const longDate = (s: string) => new Date(s + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
const shiftWeekday = (s: string, dir: number) => { const d = new Date(s + 'T00:00:00'); do { d.setDate(d.getDate() + dir); } while (d.getDay() === 0 || d.getDay() === 6); return isoLocal(d); };

const Bar: React.FC<{ value: number; target: number; big?: boolean }> = ({ value, target, big }) => {
  const p = Math.min(100, Math.round((value / Math.max(1, target)) * 100));
  const tone = p >= 90 ? 'bg-st-ok' : p >= 60 ? 'bg-hook-rose' : 'bg-st-warn';
  return (
    <div className="mt-1">
      <div className="flex items-baseline justify-between mb-1">
        <span className={`font-mono font-bold tabular-nums ${big ? 'text-[28px]' : 'text-[16px]'}`}>{value}<span className="text-faint text-[12px]"> / {target}</span></span>
        <span className="font-mono text-[10px] text-dim">{p}%{p >= 100 ? ' 🎯' : ''}</span>
      </div>
      <div className={`${big ? 'h-3' : 'h-2'} rounded-full bg-raise overflow-hidden`}><div className={`h-full ${tone} rounded-full transition-all`} style={{ width: `${p}%` }} /></div>
    </div>
  );
};

// Cellule de stat sur fond sombre (moteur Minari du jour).
const DarkStat: React.FC<{ label: string; value: React.ReactNode; hint?: string; highlight?: boolean }> = ({ label, value, hint, highlight }) => (
  <div className={`rounded-lg px-2.5 py-2 ${highlight ? 'bg-hook-rose/20 ring-1 ring-hook-rose/40' : 'bg-white/5'}`}>
    <div className="font-mono text-[8.5px] uppercase tracking-widest text-white/45">{label}</div>
    <div className="font-mono text-[18px] font-bold tabular-nums leading-none mt-0.5">{value}</div>
    {hint && <div className="font-mono text-[8px] text-white/35 mt-0.5">{hint}</div>}
  </div>
);

const Conseils: React.FC<{ c: SdrDayClient }> = ({ c }) => {
  const b = c.brief; if (!b) return null;
  const line = (label: string, xs: string[]) => xs.length ? (
    <div className="text-[11.5px]"><span className="font-mono text-[9px] uppercase tracking-widest text-faint">{label} </span><span className="text-dim">{xs.slice(0, 6).join(' · ')}</span></div>
  ) : null;
  if (!b.cible.length && !b.secteurs.length && !b.zones.length && !b.exclusions.length) return null;
  return (
    <div className="mt-2.5 rounded-lg bg-panel-2 border border-line p-2.5 space-y-1">
      <div className="font-mono text-[9px] uppercase tracking-widest text-hook-rose">🧭 Conseils du jour</div>
      {line('Cible', b.cible)}{line('Secteurs', b.secteurs)}{line('Zones', b.zones)}{line('Exclure', b.exclusions)}
    </div>
  );
};

// Carte d'un client de la journée : objectif + conseils + échanges du jour, tout au même endroit.
const ClientDayCard: React.FC<{ c: SdrDayClient; target: number }> = ({ c, target }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-card border border-line bg-panel p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[14px] font-bold truncate">{c.clientName}</div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-faint">{c.channel === 'gtm' ? '🎯 GTM' : '📞 Cold call'}{c.workLocation ? ` · ${c.workLocation}` : ''}</div>
        </div>
        <div className="text-right shrink-0 font-mono text-[11px] text-dim">{c.bookes} booké{c.bookes > 1 ? 's' : ''}</div>
      </div>
      <Bar value={c.connected} target={target} />
      <div className="flex gap-3 mt-1.5 font-mono text-[10.5px] text-dim flex-wrap">
        <span className="text-ink font-semibold">{c.connected} connectés</span><span>{c.echanges} conversations &gt;30s</span>
        {(c.channel === 'gtm' || c.emails > 0) && <span>✉️ {c.emails > 0 ? `${c.emails} mails` : <span className="text-faint">mail à connecter</span>}</span>}
      </div>
      <Conseils c={c} />
      <Link to={`/app/calibrage-guide?c=${c.clientId}`} className="inline-block mt-2 text-[11px] font-semibold text-hook-rose hover:underline">🧭 Comprendre ce client →</Link>
      {c.items.length > 0 && (
        <button onClick={() => setOpen(o => !o)} className="mt-2.5 font-mono text-[10px] uppercase tracking-widest text-hook-rose">
          {open ? '▴ masquer' : `▾ ${c.items.length} conversation${c.items.length > 1 ? 's' : ''} du jour`}
        </button>
      )}
      {open && (
        <div className="mt-2 space-y-2 border-t border-line pt-2 max-h-[40vh] overflow-y-auto">
          {c.items.map((it, i) => (
            <div key={i} className="text-[12px]">
              <div className="flex items-center gap-2"><span className="font-semibold truncate">{it.company || 'Contact'}</span>{it.contact && <span className="text-faint text-[11px] truncate">· {it.contact}</span>}</div>
              <p className="text-dim leading-snug whitespace-pre-wrap">{it.summary}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// « Ma journée » du SDR — gamifiée, basée sur le déroulé du cerveau (pack / full stack).
// Bandeau du jour (mission + objectif + checklist) puis une carte par client (objectif +
// conseils + échanges réunis). Clients tirés des call_logs (mesuré), live via realtime.
const SdrDayPage: React.FC = () => {
  const { profile } = useAuth();
  const role = profile?.role || 'sdr';
  const isInternalQA = role !== 'sdr';

  const [sdrOptions, setSdrOptions] = useState<SdrOption[]>([]);
  const [sdrId, setSdrId] = useState<string | null>(role === 'sdr' ? profile?.user_id ?? null : null);
  const [date, setDate] = useState(isoLocal(new Date()));
  const [day, setDay] = useState<SdrDay | null>(null);
  const [mn, setMn] = useState<MinariActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<Set<number>>(new Set());

  useEffect(() => { if (isInternalQA) getSdrOptions().then(o => { setSdrOptions(o); setSdrId(p => p ?? o[0]?.id ?? null); }).catch(() => {}); }, [isInternalQA]);

  const load = useCallback(async () => {
    if (!sdrId) { setLoading(false); return; }
    setLoading(true); setErr(null);
    try {
      const [d, mAll] = await Promise.all([getSdrDay(sdrId, date), getMinariActivity(date, date)]);
      setDay(d); setMn(mAll[sdrId] ?? null);
    }
    catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, [sdrId, date]);
  useEffect(() => { load(); }, [load]);
  // Charge la checklist persistée du SDR pour ce jour (vérifiable par le manager).
  useEffect(() => {
    if (!sdrId) { setDone(new Set()); return; }
    getSdrChecklist(sdrId, date).then(a => setDone(new Set(a))).catch(() => setDone(new Set()));
  }, [sdrId, date]);

  useEffect(() => {
    if (!sdrId) return;
    const ch = supabase.channel(`sdr_day_${sdrId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'call_logs', filter: `sdr_profile_id=eq.${sdrId}` }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [sdrId, load]);

  const selType = sdrOptions.find(o => o.id === sdrId)?.type;
  const isGtm = (day?.mode ?? (selType === 'gtm' ? 'gtm' : 'cold_call')) === 'gtm';
  const steps = isGtm ? GTM_STEPS : COLD_STEPS;
  const clients = day?.clients ?? [];
  const gtmTargetTotal = useMemo(() => Math.max(1, clients.filter(c => c.planned && c.channel === 'gtm').length) * GTM_TARGET, [clients]);
  const objTarget = isGtm ? gtmTargetTotal : COLD_TARGET;

  const toggle = (i: number) => setDone(s => {
    const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i);
    if (sdrId) saveSdrChecklist(sdrId, date, isGtm ? 'gtm' : 'cold_call', [...n]).catch(() => {});
    return n;
  });

  // Bande de stats Minari du jour (mesurées) — pour motiver.
  const stats = useMemo(() => {
    const s = clients.reduce((a, c) => ({ calls: a.calls + c.calls, conn: a.conn + c.connected, ech: a.ech + c.echanges, ech2: a.ech2 + c.echanges2min, book: a.book + c.bookes }), { calls: 0, conn: 0, ech: 0, ech2: 0, book: 0 });
    return s;
  }, [clients]);
  const pct = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) : 0);

  return (
    <div>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
        <h1 className="text-[21px] font-bold tracking-tight">Ma journée</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {isInternalQA && (
            <label className="flex items-center gap-1.5 text-[12px] text-dim">
              <span className="font-mono text-[9px] uppercase tracking-widest text-faint">SDR</span>
              <select value={sdrId ?? ''} onChange={e => setSdrId(e.target.value || null)} className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px] max-w-[220px]">
                {!sdrId && <option value="">— choisir —</option>}
                {sdrOptions.map(o => <option key={o.id} value={o.id}>{o.name} · {TYPE_LABEL[o.type]}</option>)}
              </select>
            </label>
          )}
          <div className="flex items-center gap-1">
            <button onClick={() => setDate(shiftWeekday(date, -1))} className="w-8 h-8 rounded-md border border-line-2 hover:border-ink">‹</button>
            <span className="text-[12.5px] font-semibold capitalize min-w-[150px] text-center">{longDate(date)}</span>
            <button onClick={() => setDate(shiftWeekday(date, 1))} className="w-8 h-8 rounded-md border border-line-2 hover:border-ink">›</button>
          </div>
        </div>
      </div>

      {err && <div className="mb-4 text-[13px] text-st-bad bg-st-bad/10 border border-st-bad/20 rounded-lg px-3 py-2">{err}</div>}

      {!sdrId ? (
        <EmptyState title="Aucun SDR sélectionné" hint="Choisis un SDR pour voir sa journée." />
      ) : loading ? (
        <div className="bg-panel border border-line rounded-card p-5"><SkeletonRows count={5} /></div>
      ) : (
        <>
          {/* BANDEAU DU JOUR — mission + objectif + checklist gamifiée */}
          <div className="bg-hook-dark text-white rounded-card p-5 mb-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`font-mono text-[10px] font-bold px-2 py-0.5 rounded ${selType === 'gtm' ? 'bg-emerald-400 text-hook-dark' : selType === 'cold_call' ? 'bg-hook-rose text-white' : 'bg-white/20 text-white'}`}>
                    SDR {selType && selType !== '?' ? TYPE_LABEL[selType].toUpperCase() : '?'}
                  </span>
                  <span className="font-mono text-[9px] uppercase tracking-widest text-white/50">Aujourd'hui</span>
                </div>
                <div className="text-[18px] font-bold capitalize">{longDate(date)}</div>
                <div className="mt-1 flex gap-1.5 flex-wrap">
                  {(day?.missions ?? []).length === 0 ? <span className="text-[12px] text-white/50">Aucune mission planifiée à l'agenda</span>
                    : day!.missions.map((m, i) => <span key={i} className="font-mono text-[10px] px-2 py-0.5 rounded bg-white/10">{m}</span>)}
                </div>
                <div className="mt-2 text-[12.5px] font-semibold">
                  🎯 Objectif du jour : {isGtm
                    ? `${GTM_TARGET} connectés par client · ${clients.filter(c => c.planned && c.channel === 'gtm').length || '—'} client(s) suivi(s)`
                    : `${COLD_TARGET} connectés · ~2 à 3 RDV`}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-[9px] uppercase tracking-widest text-white/50">Connectés du jour</div>
                <div className="text-[30px] font-bold font-mono leading-none tabular-nums">{mn ? mn.connectes : stats.conn}<span className="text-white/40 text-[14px]"> / {objTarget}</span></div>
                <div className="font-mono text-[10px] text-white/60 mt-1">
                  {/* La cible OFFICIELLE de la journée-type (1 135 journées analysées) : 30-50 conversations,
                      et la bascule à 2 min (conversion ×6 entre 1-2 min et 2-4 min). */}
                  {!isGtm
                    ? <><b className="text-white/90">{day?.echangesTotal ?? 0}/40</b> conversations (cible 30-50) · {stats.ech2} &gt; 2 min · {day?.bookesTotal ?? 0} bookés</>
                    : <>{day?.echangesTotal ?? 0} conversations &gt;30s · {day?.bookesTotal ?? 0} bookés</>}
                </div>
              </div>
            </div>
            {/* Volume Minari (API directe) — le VRAI nombre de numéros composés + le temps de connexion. */}
            {mn && (
              <div className="mt-4 grid grid-cols-3 gap-2">
                <DarkStat label="Connectés" value={mn.connectes} hint={mn.connectRate != null ? `${Math.round(mn.connectRate)}% connect` : 'décrochés'} highlight />
                <DarkStat label="Composés (Minari)" value={mn.composes} hint="tous les numéros" />
                <DarkStat label="Temps de connexion" value={`${mn.sessionMin}′`} hint="dialing actif" />
              </div>
            )}
            {/* Moteur de stats du jour — call (connecté < 30 s) vs échange (> 30 s). */}
            <div className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-2">
              <DarkStat label="Appels loggés" value={stats.calls} hint="toutes lignes du jour" />
              <DarkStat label="< 30 s" value={Math.max(0, stats.calls - stats.ech)} hint="conversations courtes" />
              <DarkStat label="Conversations" value={stats.ech} hint="connecté > 30s (dico)" highlight />
              <DarkStat label="+ de 2 min" value={stats.ech2} />
              <DarkStat label="Bookés" value={stats.book} />
              <DarkStat label="Éch → booké" value={`${pct(stats.book, stats.ech)}%`} />
            </div>
            <p className="mt-1.5 font-mono text-[9px] text-white/40">{mn ? <><b className="text-white/70">Composé</b> = numéro appelé (Minari) ; </> : null}un <b className="text-white/70">connecté</b> = appel décroché ; une <b className="text-white/70">conversation</b> = connecté &gt; 30 s (l'unité qui compte).{!mn && ' Composés indisponibles (Minari).'}</p>

            {/* Checklist du déroulé */}
            <div className="mt-4 border-t border-white/10 pt-3">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[9px] uppercase tracking-widest text-white/50">Déroulé de la journée</span>
                <span className="font-mono text-[10px] text-white/60">{done.size}/{steps.length} ✓</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {steps.map((s, i) => (
                  <button key={i} onClick={() => toggle(i)}
                    className={`text-[11px] px-2 py-1 rounded-md border transition-colors ${done.has(i) ? 'bg-st-ok/20 border-st-ok/40 text-white line-through' : 'border-white/20 text-white/80 hover:border-white/50'}`}>
                    {done.has(i) ? '✓ ' : `${i + 1}. `}{s}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* CLIENTS DU JOUR — objectif + conseils + échanges réunis */}
          {clients.length === 0 ? (
            <EmptyState title="Aucune activité ce jour" hint="Navigue vers un autre jour (données dev figées au 22/07) ou choisis un autre SDR." />
          ) : (
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-dim">Mes missions aujourd'hui · {clients.filter(c => c.planned).length}</h2>
              <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-st-ok"><span className="w-1.5 h-1.5 rounded-full bg-st-ok animate-pulse" />live</span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {clients.filter(c => c.planned).map(c => <ClientDayCard key={c.clientId} c={c} target={c.channel === 'gtm' ? GTM_TARGET : COLD_TARGET} />)}
          </div>
          {/* Hors mission : confirmations de RDV, dépannage — visibles mais HORS objectifs (réunion GTM 10/09). */}
          {clients.some(c => !c.planned) && (
            <>
              <h2 className="font-mono text-[10px] uppercase tracking-widest text-faint mt-5 mb-2">Hors mission (confirmations, dépannage) — ne compte pas dans les objectifs</h2>
              <div className="flex flex-wrap gap-2">
                {clients.filter(c => !c.planned).map(c => (
                  <span key={c.clientId} className="inline-flex items-center gap-2 border border-line rounded-lg px-2.5 py-1.5 text-[12px] bg-panel opacity-80">
                    <span className="font-semibold">{c.clientName}</span>
                    <span className="font-mono text-[10px] text-dim">{c.echanges} éch. · {c.calls} conv.{c.bookes ? ` · ${c.bookes} booké${c.bookes > 1 ? 's' : ''}` : ''}</span>
                  </span>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SdrDayPage;
